import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
  Optional,
  HostListener,
  OnChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { PhxOptionComponent } from './option.component';
import { SelectFocusableItemDirective } from './selectFocusableItem.directive';
import { SelectFocusTrapItemDirective } from './selectFocusTrapItem.directive';
import {
  PhxSelectOption,
  PhxSelectSize,
  PhxSelectMultiDisplayMode,
  PhxSelectValue,
  PhxTriggerType,
  PhxSelectBackground,
} from './select.types';

import { PhxDataSource } from '../services/data-source/phx-data-source.service';

import mixinDisabled from '../../mixins/disabled';
import { PHX_SELECT_OPTION_TOKEN } from './select.token';
import { KeyboardKeys } from '../../helpers/KeyboardKeys.enum';
import { PhxContextService, PHOENIX_CONTEXTS } from '../../services/phx-context-service';
import { PhxComponents } from '../../types';
import { POSITION_TYPE } from '../dropdown/overlay-position';
import { PhxDropdownDirective } from '../dropdown/phx-dropdown.directive';
import { ChevronDown, Cross } from './../icon/icon-map';
import { debounce, deepCompareValues } from '../../utils';
import { PhxSelectionListComponent } from './selection-list.component';
import { PhxSelectDefaultConfig } from './phx-select.common';
import { PhxDropdownOffset } from '../dropdown/phx-dropdown.types';
import { PhxStatsigService } from '../../services/phx-statsig.service';
import { PHX_STATSIG_KEYS, PHX_BACKGROUND } from '../../constants';

const baseSelectMixin = mixinDisabled(
  class {
    constructor(public _elementRef: ElementRef) {}
  },
);

@Component({
  selector: 'phx-select',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhxSelectComponent),
      multi: true,
    },
  ],
  templateUrl: 'select.component.html',
  styleUrls: ['./select.component.scss'],
  host: {
    class: 'phx-select',
    '[class.phx-select-dropdown-open]': 'phxOpen && !disabled',
    '[class.phx-select-dropdown-focused]': '(phxOpen || focused) && !disabled',
    '[class.phx-select-disabled]': 'disabled',
    '[class.phx-select-selected]': 'hasSelectedOptions',
    '[class.phx-select-has-prefix-icon]': 'phxPrefixIcon',
    '[class.phx-select-has-search]': 'phxShowSearch',
    '[class.phx-select-large]': `phxSize === 'large' && phxTriggerType !== 'ghost'`,
    '[class.phx-select-medium]': `phxSize === 'medium' && phxTriggerType !== 'ghost'`,
    '[class.phx-select-small]': `phxSize === 'small' && phxTriggerType !== 'ghost'`,
    '[class.phx-select-extra-small]': `phxSize === 'extra-small' && phxTriggerType !== 'ghost'`,
    '[class.phx-select-multi-tag-mode]': `phxMultiple && phxDisplayTypeInMultiMode === 'tag'`,
    '(keydown)': 'onTriggerKeydown($event)',
    '[class.phx-filter]': 'phxStyleType === PHOENIX_CONTEXTS.FILTER',
    '[class.phx-select-on-dark]': 'phxBackground === PHX_BACKGROUND.DARK',
    '[class.phx-sort-item]': 'isSortContext',
    '[class.phx-select-has-error]': 'phxHasError',
    '[class.phx-select-ghost]': `phxTriggerType === 'ghost'`,
    '[attr.tabindex]': 'disabled ? null : 0',
    '[attr.phx-non-default-selected]': 'isNonDefaultSelected',
    role: 'menu',
  },
  inputs: ['disabled'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class PhxSelectComponent
  extends baseSelectMixin
  implements AfterViewInit, OnDestroy, ControlValueAccessor, OnChanges
{
  @ViewChild('dropdownRef', { static: false, read: PhxDropdownDirective }) dropdownRef: PhxDropdownDirective;
  @ViewChild(PhxSelectionListComponent) optionList: PhxSelectionListComponent;
  @ContentChildren(PHX_SELECT_OPTION_TOKEN as any) listOfPhxOptions: QueryList<PhxOptionComponent>;
  @ViewChildren(SelectFocusableItemDirective) listItems: QueryList<SelectFocusableItemDirective>;
  @ViewChildren(SelectFocusTrapItemDirective) focusTrapElements: QueryList<SelectFocusTrapItemDirective>;

  @Input() public phxOptions: PhxSelectOption[];
  @Input() public phxDataSource: PhxDataSource<PhxSelectOption[]>;
  @Input() public phxVirtualScrollEnabled: boolean = true;
  @Input() public phxTruncateSubText: boolean = true;
  @Input() public phxPlaceholder: string | TemplateRef<any>;
  @Input() public phxSearchPlaceholder: string = 'Search';
  @Input() public phxPrefixIcon: TemplateRef<any>;
  @Input() public phxOpen = false;
  @Input() public phxPlacement: POSITION_TYPE = 'bottomLeft';
  @Input() public phxAllowClear = true;
  @Input() public phxShowSearch = false;
  @Input() public phxFocusSearchOnOpen = true;
  @Input() public phxCloseOnClear = false;
  @Input() public phxLoading = false;
  @Input() public phxLabelTemplate: TemplateRef<{ $implicit: any }>;
  @Input() public phxTriggerTemplate: TemplateRef<{ $implicit: PhxSelectOption | PhxSelectOption[] }>;
  @Input() public phxMultiple: boolean = false;
  @Input() public phxResultNotFound: TemplateRef<any> | string;
  @Input() public phxShowRadio: boolean = false;
  @Input() public phxShowSubText: boolean = false;
  @Input() public phxFilterPredicate: (searchTerm: string, opt: PhxSelectOption) => boolean;
  @Input() public phxOptionHeight: number; // phxOptionHeight is not working, see this
  @Input() public phxDropdownHeight: number = PhxSelectDefaultConfig.maxHeightOfDropdown;
  @Input() public phxDropdownWidth: number | string = null;
  @Input() public phxDropdownOffset: PhxDropdownOffset = { x: 0, y: 0 };
  @Input() public phxSize: PhxSelectSize = 'medium';
  @Input() public phxDropdownClassName: string = '';
  @Input() public phxDisplayTypeInMultiMode: PhxSelectMultiDisplayMode = 'badge';
  @Input() public phxEmitOnlyIndependentSelections: boolean = false;
  @Input() public phxSearchDebounceTime: number = 300; //done
  @Input() public phxSelectionChangeDebounceTime: number = null;
  @Input() public phxOptimisticSelection: boolean = false;
  @Input() public phxServerSearch: boolean = false;
  @Input() public phxEnableAddNewOption: boolean = false;
  @Input() public phxNewOptionValidation: (option: string) => boolean;
  @Input() public phxDefaultValue: any;
  @Input() public phxShowSelectCrossIcon: boolean = false;
  @Input() public phxHasError: boolean = false;
  @Input() public phxFooterRightTemplate: TemplateRef<any>;
  @Input() public phxPreventSelection: boolean = false;
  @Input() public phxHighlightSubtext: boolean = false;
  @Input() public phxTriggerType: PhxTriggerType = 'default';
  @Input() public phxSearchText: string;
  @Input() public phxShowFirstOptionFocused: boolean = true;
  @Input() public phxBackground: PhxSelectBackground = PHX_BACKGROUND.STANDARD;
  @Input() public phxDisableParentSelection: boolean = false;
  @Input() public phxShowOnlyIndependentSelections: boolean = false;
  @Input()
  public get phxShowCounter(): boolean {
    return this._showCounter && this.phxMultiple && this.optionList?.selectedOptionCount >= 2;
  }
  public set phxShowCounter(value: boolean) {
    this._showCounter = value;
  }

  @Output() public readonly phxLoadingChange = new EventEmitter<boolean>();
  @Output() public readonly phxOpenChange = new EventEmitter<boolean>();
  @Output() public readonly phxSearchChange = new EventEmitter<string>();
  @Output() public readonly phxSelectionChange = new EventEmitter<PhxSelectOption | PhxSelectOption[]>();
  @Output() public readonly phxClear = new EventEmitter<void>();
  @Output() public readonly phxScrollEnd = new EventEmitter<void>();
  @Output() public readonly phxFocus = new EventEmitter<void>();
  @Output() public readonly phxBlur = new EventEmitter<void>();
  @Output() public readonly phxAddOptionClick = new EventEmitter<PhxSelectOption>();
  @Output() public readonly phxLinkOptionClick = new EventEmitter<PhxSelectOption>();

  @HostListener('window:keydown', ['$event'])
  public onWindowKeydown(event: KeyboardEvent): void {
    if (this.phxOpen) {
      switch (event.code) {
        case KeyboardKeys.ESC:
          event.preventDefault();
          this.onDropdownToggle(false);
          this.focusTriggerElement();
          break;
      }
    }
  }

  public get hasSelectedOptions(): boolean {
    return !!this.optionList?.selectedOptions?.length;
  }

  public selectedValue: any;
  public searchedOptions: PhxSelectOption[];
  public searched$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public focused: boolean;
  public readonly PHOENIX_CONTEXTS = PHOENIX_CONTEXTS;
  public readonly PHX_BACKGROUND = PHX_BACKGROUND;
  public phxStyleType: PHOENIX_CONTEXTS;
  public ChevronDownIcon: string = ChevronDown;
  public CrossIcon: string = Cross;
  public dropdownWidth: string | number = 'auto';
  public onChange = val => {};
  public onTouched = () => {};
  private _showCounter: boolean = true;
  private _destroy$ = new Subject<void>();
  private _statsigServiceDestroy$ = new Subject<void>();
  private _onChangeRef: (val: any) => void;

  public get isNonDefaultSelected(): boolean {
    if (!this.phxDefaultValue) return this.optionList?.selectedOptions.length > 0;
    const selectedValue = [...(this.selectedValue || [])];
    const defaultValue = [...(this.phxDefaultValue || [])];
    return !deepCompareValues(selectedValue, defaultValue);
  }

  private focusTriggerElement(): void {
    setTimeout(() => this._host.nativeElement?.focus(), 80);
  }

  public get selectLabel(): string | TemplateRef<any> {
    return this.optionList?.selectedItemsCount >= 2 ? this.phxPlaceholder : this.optionList?.firstSelectedOption?.label;
  }

  private transformProjectedOptions(content: PhxOptionComponent[]): PhxSelectOption[] {
    return content.map(
      (item: PhxOptionComponent): PhxSelectOption => ({
        label: item.label,
        value: item.value,
        subText: item.subText,
        groupLabel: item.groupLabel,
        isDivider: item.isDivider,
        isLink: item.isLink,
        disabled: item.disabled,
        data: item.data,
        optionTemplate: item.phxOptionTemplate,
        prefixIcon: item.prefixIcon,
        tooltip: item.tooltip,
        suffixTemplate: item.suffixTemplate,
      }),
    );
  }

  private get isFilterContext(): boolean {
    return this.phxStyleType === PHOENIX_CONTEXTS.FILTER || this.isSortContext;
  }

  public get shouldFocusSearch(): boolean {
    return this.phxOpen && this.phxShowSearch && this.phxFocusSearchOnOpen;
  }

  public get dropdownClasses(): string {
    let classes = this.phxDropdownClassName;
    if (!this.isFilterContext || this.phxDropdownWidth) return classes;
    if (this.optionList?.options?.length > this.optionList?.phxMinBufferMultiplier && !this.isSortContext)
      classes += ' phx-select-fix-width';
    else classes += ' phx-select-min-max-width';
    return classes;
  }

  public get isSortContext(): boolean {
    return this.phxStyleType === PHOENIX_CONTEXTS.SORT_ITEM;
  }

  public onValueChange(value: any) {
    this.selectedValue = value;
    this.onChange(value);
  }

  public onClear() {
    if (this.phxCloseOnClear) this.onDropdownToggle(false);
    this.phxClear.emit();
  }

  public setDropdownWidth(): void {
    if (this.phxDropdownWidth) {
      this.dropdownWidth = this.phxDropdownWidth;
    } else if (this.phxTriggerType === 'ghost' || this.isSortContext) {
      this.dropdownWidth = 'auto';
    } else if (!this.isFilterContext) {
      this.dropdownWidth = this._host.nativeElement.getBoundingClientRect().width;
    } else if (this.optionList?.options?.length > this.optionList?.phxMinBufferMultiplier) {
      this.dropdownWidth = PhxSelectDefaultConfig.maxWidthOfDropdown;
    }
  }

  public writeValue(value: PhxSelectValue | PhxSelectOption | PhxSelectValue[] | PhxSelectOption[]): void {
    this.selectedValue = value;
    this._cdr.markForCheck();
  }

  public registerOnChange(fn: any): void {
    this._onChangeRef = fn;
    if (this.phxSelectionChangeDebounceTime) {
      this.onChange = debounce(fn, this.phxSelectionChangeDebounceTime);
    } else {
      this.onChange = fn;
    }
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public removeTag(event: MouseEvent | KeyboardEvent, option: PhxSelectOption, index: number): void {
    event.stopPropagation();
    this.optionList?.handleOptionClick(option, index);
  }

  public onOptionClick() {
    if (!this.phxMultiple) this.onDropdownToggle(false);
    if (this.phxDisplayTypeInMultiMode === 'tag' && this.phxMultiple) this.dropdownRef.updateOverlayPosition();
  }

  public onFocusTrigger(): void {
    setTimeout(() => this._host.nativeElement?.focus(), 0);
  }

  public onAddOptionClick(option: PhxSelectOption): void {
    this.onDropdownToggle(false);
    // Added delay to properly close dropdown before adding new option to prevent flickering
    setTimeout(() => {
      this.phxAddOptionClick.emit(option);
    }, 100);
  }

  public onLinkOptionClick(option: PhxSelectOption): void {
    this.phxLinkOptionClick.emit(option);
  }

  public onDropdownToggle(state: boolean): void {
    this.phxOpen = state;
    if (this.phxOpen) this.optionList?.adjustViewportHeightForOptions();
    // TODO: investigate and fix this 100ms timeout
    setTimeout(() => {
      if (this.shouldFocusSearch) this.optionList?.focusSearchInput();
      else if (!this.phxVirtualScrollEnabled) {
        this.optionList?.onOptionsFocus(); // Will focus the active options if any, otherwise will focus the first option
      } else this.optionList?.focusVirtualScrollViewport();
      this.optionList?.virtualScrollToIndex();
    }, 100);
    this.phxOpenChange.emit(state);
    this.setDropdownWidth();

    if (!state) {
      this.onTouched();
    }
  }

  public onTriggerKeydown(event: KeyboardEvent): void {
    if (!this.phxOpen) {
      switch (event.code) {
        case KeyboardKeys.ENTER:
        case KeyboardKeys.SPACE:
          event.preventDefault();
          this.onDropdownToggle(true);
          this.optionList.focus();
          break;
      }
    }
  }

  constructor(
    private _host: ElementRef<HTMLElement>,
    private _cdr: ChangeDetectorRef,
    @Optional() private _contextService: PhxContextService,
    private statsigService: PhxStatsigService,
  ) {
    super(_host);
    this.phxStyleType = _contextService?.getType();
    if (this._contextService) this._initContainerContext();
  }
  
  private updateSize(size: PhxSelectSize): void {
    this.phxSize = size;
    this._cdr.markForCheck();
  }

  /** Subscribes to the container context size map if this component is inside a container (e.g. phx-overview). */
  private _initContainerContext(): void {
    if (this._contextService?.getType() !== PHOENIX_CONTEXTS.CONTAINER_COMPONENT) {
      return;
    }
    const containerService = this._contextService;
    containerService.sizeMap$.pipe(takeUntil(this._destroy$)).subscribe(sizeMap => {
      const size = containerService.getSizeForSelector(sizeMap, PhxComponents.Select) as PhxSelectSize | null;
      if (size) {
        this.updateSize(size);
      }
    });
  }

  ngOnInit(): void {
    this.statsigService
      .getConfigs()
      .pipe(takeUntil(this._statsigServiceDestroy$))
      .subscribe(config => {
        if (config[PHX_STATSIG_KEYS.SORT_CONSISTENCY_ENABLED] && this.isSortContext) {
          this.phxPlacement = 'bottomRight';
        }
      });
  }

  ngOnChanges(simpleChanges: SimpleChanges): void {
    const { phxDropdownHeight, phxOptionHeight, phxDefaultValue } = simpleChanges;

    if (phxDropdownHeight) {
      this.optionList?.adjustViewportHeightForOptions();
    }

    if (phxOptionHeight) {
      this.phxOptionHeight = this.phxOptionHeight || PhxSelectDefaultConfig.optionHeight;
      this.optionList?.adjustViewportHeightForOptions();
    }

    if (phxDefaultValue && !this.selectedValue) {
      this.selectedValue = phxDefaultValue.currentValue;
      this.onChange(phxDefaultValue.currentValue);
    }
  }

  ngAfterContentInit(): void {
    this.phxDropdownClassName = this.phxDropdownClassName + ' phx-select-dropdown';
    if (this.listOfPhxOptions.length) {
      this.phxOptions = this.transformProjectedOptions(this.listOfPhxOptions.toArray());
      this._cdr.detectChanges();
    }
    this.listOfPhxOptions.changes.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.phxOptions = this.transformProjectedOptions(this.listOfPhxOptions.toArray());
      this._cdr.detectChanges();
    });
  }

  public ngAfterViewInit(): void {
    this.setDropdownWidth();
    this._cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  clearSelection(event: Event): void {
    event.stopPropagation();
    this.optionList.clearOption();
  }
}
